import { useState, useEffect, useRef } from 'react';
import { Escort } from '../types';
import { fetchEscorts, searchEscorts, getEscortById, SearchCriteria } from '../data/escorts';
import { useCountryContext } from '../contexts/CountryContext';
import { logDebug } from '../utils/log';

interface UseEscortsReturn {
  escorts: Escort[];
  loading: boolean;
  error: string | null;
  apiConnected: boolean;
  refetch: () => Promise<void>;
  searchByLocation: (location: string) => Promise<void>;
  searchByFilter: (criteria: SearchCriteria) => Promise<Escort[]>;
  getById: (id: string) => Promise<Escort | null>;
  getFeaturedEscorts: () => Escort[];
  getVetrinaEscorts: () => Escort[];
  getRegularEscorts: () => Escort[];
}

export const useEscortsData = (): UseEscortsReturn => {
  const [escorts, setEscorts] = useState<Escort[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [apiConnected, setApiConnected] = useState(false);
  const [currentCountry, setCurrentCountry] = useState<string>('');
  const expectedCountryRef = useRef<string>('');
  const { getCountryModel, selectedCountry } = useCountryContext();

  const loadEscorts = async () => {
    const countryToLoad = selectedCountry;
    const countryModel = getCountryModel();
    
    // Previeni caricamenti duplicati per lo stesso paese
    if (loading && currentCountry === countryToLoad) {
      logDebug(`⏸️ Salto caricamento duplicato per ${countryToLoad}`);
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      setCurrentCountry(countryToLoad);
      expectedCountryRef.current = countryToLoad;
      
      logDebug(`🚀 Inizio caricamento ${countryModel} per ${countryToLoad}`);
      
      logDebug('🔄 Tentativo di caricamento escort dall\'API...');
      logDebug('📍 URL API:' + import.meta.env.VITE_API_URL || 'non configurato');
      logDebug('🔑 Token API:' + import.meta.env.VITE_API_TOKEN ? 'configurato' : 'non configurato');

      const data = await fetchEscorts(countryModel);

      logDebug('✅ Dati ricevuti dall\'API:' + data.length + 'escort');
      logDebug('📊 Primo escort:' + data[0]?.name || 'nessun dato');

      // Verifica che il paese target sia ancora quello che ci aspettiamo
      const expectedCountry = expectedCountryRef.current;
      logDebug(`🔍 Controllo: caricati per ${countryToLoad}, atteso: ${expectedCountry}, selectedCountry: ${selectedCountry}`);
      
      if (countryToLoad === expectedCountry) {
        logDebug(`✅ Caricati ${data.length} escort per ${countryToLoad} (${countryModel})`);
        setEscorts(data);
        setApiConnected(true);
      } else {
        logDebug(`🚫 Scarto dati ${countryModel} per ${countryToLoad} - ora atteso: ${expectedCountry}`);
      }
      logDebug('✅ Escort impostati nel state');
      logDebug('🔌 API connessa:' + true);
    } catch (err) {
      logDebug('❌ Errore nel caricamento escort:' + err);
      setError('Errore nel caricamento dei dati dall\'API');
      setEscorts([]);
      setApiConnected(false);
    } finally {
      setLoading(false);
    }
  };

  const searchByLocation = async (location: string) => {
    try {
      setLoading(true);
      setError(null);
      const countryModel = getCountryModel();
      const data = await searchEscorts({ location }, countryModel);
      setEscorts(data);
      setApiConnected(true);
    } catch (err) {
      logDebug('Search failed:' + err);
      setError('Errore nella ricerca per location');
      setEscorts([]);
      setApiConnected(false);
    } finally {
      setLoading(false);
    }
  };

  const searchByFilter = async (criteria: SearchCriteria): Promise<Escort[]> => {
    try {
      setLoading(true);
      setError(null);
      const countryModel = getCountryModel();
      const data = await searchEscorts(criteria, countryModel);
      setEscorts(data);
      setApiConnected(true);
      return data;
    } catch (err) {
      logDebug('Filter search failed:' + err);
      setError('Errore nel filtro di ricerca');
      setEscorts([]);
      setApiConnected(false);
      return [];
    } finally {
      setLoading(false);
    }
  };

  const getById = async (id: string): Promise<Escort | null> => {
    try {
      const countryModel = getCountryModel();
      return await getEscortById(id, countryModel);
    } catch (err) {
      logDebug('Failed to get escort by ID:' + err);
      return null;
    }
  };

  const getFeaturedEscorts = (): Escort[] => {
    return escorts.filter(escort => escort.featured);
  };

  const getVetrinaEscorts = (): Escort[] => {
    return escorts.filter(escort => escort.isVetrina);
  };

  const getRegularEscorts = (): Escort[] => {
    return escorts.filter(escort => !escort.isVetrina);
  };


  useEffect(() => {
    // Carica solo se il paese è cambiato o se non abbiamo dati
    if (currentCountry !== selectedCountry || escorts.length === 0) {
      logDebug(`🌍 useEscortsData: Cambio paese ${currentCountry} → ${selectedCountry}`);
      expectedCountryRef.current = selectedCountry;
      loadEscorts();
    }
  }, [selectedCountry]);

  return {
    escorts,
    loading,
    error,
    apiConnected,
    refetch: loadEscorts,
    searchByLocation,
    searchByFilter,
    getById,
    getFeaturedEscorts,
    getVetrinaEscorts,
    getRegularEscorts,
  };
};

export default useEscortsData;
